import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { DragDropContext } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';

import EventDeskTabLayout from '../EventDeskTabLayout';
import DrawAndMatchList from './DrawAndMatchList';
import NextAvailableHeader from './NextAvailableHeader';
import NextAvailableCalendar from './NextAvailableCalendar';
import { getActiveFacilitiesAndCourts } from '../../../utils/courtSchedular';
import { assignMatchToCourt, clearNextAvailableData, clearPostScoreErrors, confirmActionFromConfirmPopup, declineActionFromConfirmPopup, getNextAvailableConfigData, moveMatchInSidePanel, postScoreForAMatch, reAssignMatchToCourt, toggleAddDrawsSidePanel, unAssignMatchFromCourt, updateSaveConfigViewDisplay, setSidebarDisplayAndStatusTypes } from '../../../actions/next-available-actions';
import NextAvailableConfig from './NextAvailableConfig';

import styles from './nextAvailable.scss';
import AddDrawsSidePanel from './AddDrawsSidePanel';
import DrawEditConfig from '../CourtSchedular/DrawEditConfig';
import { drawScheduleTypeIds } from '../../../utils/eventConstants';
import MatchStartedPopup from './MatchStartedPopup';
import MatchPostScoreSidePanel from './MatchPostScoreSidePanel';
import {
    ConfirmPopupTypes,
    DrawStatusTypes,
    MatchStatusTypes,
    SideBarDisplayTypes } from '../../../utils/nextAvailable';
import ConfirmationPopup from './ConfirmationPopup';

const NextAvailable = (props) => {
    const { openDrawInEditor } = props;
    const dispatch = useDispatch();
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);
    const { actionConfirmPopup } = nextAvailable;
    const {
        event,
        locations,
        players,
        teams,
        officials,
        drawDivision,
    } = useSelector(({ drawEdit }) => {
        const { event, players, teams, locations, officials, drawDivision } = drawEdit;
        return {
            event: event?.data,
            players,
            teams,
            locations: locations?.data,
            officials: officials?.data,
            drawDivision,
        };
    });

    const [drawToEditSettings, setDrawToEditSettings] = useState();
    const [matchToPostScore, setMatchToPostScore] = useState(null);
    const activeFacilitiesAndCourts = getActiveFacilitiesAndCourts(locations || []);

    const closeEditCourtDeskConfig = () => {
        dispatch(updateSaveConfigViewDisplay(false));
    };

    useEffect(() => {
        if (event && locations?.length > 0) {
            const { id, eventSchedule } = event;

            const currentDate = moment().tz(eventSchedule.timeZone?.value).startOf('day');
            const eventStartDate = moment.utc(eventSchedule.eventStartUtc).tz(eventSchedule.timeZone?.value).startOf('day');
            const eventEndDate = moment.utc(eventSchedule.eventEndUtc).tz(eventSchedule.timeZone?.value).startOf('day');

            const initialDate = currentDate.isBefore(eventStartDate) || currentDate.isAfter(eventEndDate)
                    ? eventStartDate
                    : currentDate;
            dispatch(getNextAvailableConfigData(id, initialDate.toISOString(), true));
        }
    }, [event, locations]);

    useEffect(() => {
        return () => {
            dispatch(clearNextAvailableData());
        };
    }, []);

    const onChangeDate = (date) => {
        const { eventSchedule } = event;

        const selectedDate = moment(date).tz(eventSchedule.timeZone?.value).startOf('day').toISOString();
        dispatch(getNextAvailableConfigData(event.id, selectedDate, true));
    };

    const onEditConfig = () => {
        dispatch(updateSaveConfigViewDisplay(true));
    };

    const onRefresh = async () => {
        await dispatch(getNextAvailableConfigData(event.id, nextAvailable?.selectedDate, true));
        if (nextAvailable?.sidebar.displayType === SideBarDisplayTypes.DRAWS) {
            const status = nextAvailable?.sidebar.statusType || DrawStatusTypes.TODO;
            dispatch(setSidebarDisplayAndStatusTypes(SideBarDisplayTypes.DRAWS, status));
        } else if (nextAvailable?.sidebar.displayType === SideBarDisplayTypes.MATCHES) {
            const status = nextAvailable?.sidebar.statusType || MatchStatusTypes.IN_QUEUE;
            dispatch(setSidebarDisplayAndStatusTypes(SideBarDisplayTypes.MATCHES, status));
        }
    };

    const onDragEnd = (result) => {
        if (!result?.destination) {
            return;
        }

        if (result.destination.droppableId === result.source.droppableId) {
            if (result.source.droppableId === 'match-container') {
                dispatch(moveMatchInSidePanel(result.draggableId, result.source.index, result.destination.index));
            } else {
                // DO nothing
            }
        }

        if (result.destination.droppableId !== result.source.droppableId) {
            const matchId = result.draggableId;
            if (result.source.droppableId === 'match-container') {
                const destinationCourtId = result.destination.droppableId;

                dispatch(assignMatchToCourt(event?.id, matchId, result.source.index, destinationCourtId));
            } else if (result.destination.droppableId === 'match-container') {
                const sourceCourtId = result.source.droppableId;
                dispatch(unAssignMatchFromCourt(event?.id, matchId, sourceCourtId, result.destination.index));
            } else {
                const destinationCourtId = result.destination.droppableId;
                const sourceCourtId = result.source.droppableId;
                dispatch(reAssignMatchToCourt(event?.id, matchId, sourceCourtId, destinationCourtId));
            }
        }
    };

    const onClickAddDraws = () => {
        dispatch(toggleAddDrawsSidePanel());
    };

    const onDrawSettingsEdit = (draw) => {
        setDrawToEditSettings(draw);
    };

    const closeDrawSettingsEdit = () => {
        setDrawToEditSettings(null);
    };

    const onPostScoreClick = (match) => {
        setMatchToPostScore(match);
    };

    const onClosePostScore = () => {
        setMatchToPostScore(null);
        dispatch(clearPostScoreErrors());
    };

    const onPostScoreSubmit = (eventId, matchId, requestBody) => {
        dispatch(postScoreForAMatch(eventId, matchId, requestBody));
    };

    const onCancelConfirmationPopup = () => {
        dispatch(declineActionFromConfirmPopup());
    };

    const onConfirmConfirmationPopup = () => {
        dispatch(confirmActionFromConfirmPopup());
    };

    const getDestinationCourtName = (destinationCourtId) => {
        const destinationCourt = nextAvailable.courtsOfSelectedFacility?.find((court) => court.id === destinationCourtId);
        return destinationCourt?.name;
    };

    useEffect(() => {
      if (nextAvailable.postScore?.success) {
        onClosePostScore();
      }
    }, [nextAvailable.postScore?.success]);

    return (
        <EventDeskTabLayout
            noPadding
            fitScreen={true}
        >
            <DragDropContext
                onDragEnd={onDragEnd}
            >
                <div className={`${styles.nextAvailable} d-flex`}>
                    <DrawAndMatchList
                        eventId={event?.id}
                        event={event}
                        onClickAddDraws={onClickAddDraws}
                        onDrawSettingsEdit={onDrawSettingsEdit}
                        playersDictByMemberId={players?.playersDictByMemberId}
                        teamsDict={teams?.teamsDict}
                        onPostScoreClick={onPostScoreClick}
                        openDrawInEditor={openDrawInEditor}
                    />
                    <div className={`${styles.courtGrid} flex-1 mw0`}>
                        {nextAvailable.selectedDate && (
                            <NextAvailableHeader
                                onClickRefresh={onRefresh}
                                onClickSettings={onEditConfig}
                                onChangeDate={onChangeDate}
                                event={event}
                            />
                        )}
                        {nextAvailable.selectedDate && nextAvailable.selectedDateCourtConfig && (
                            <NextAvailableCalendar
                                eventId={event?.id}
                                sportTypeId={event?.sportTypeId}
                                playersDictByMemberId={players?.playersDictByMemberId}
                                teamsDict={teams?.teamsDict}
                                eventSchedule={event?.eventSchedule}
                                onPostScoreClick={onPostScoreClick}
                            />
                        )}
                    </div>
                </div>
            </DragDropContext>
            {nextAvailable.showConfigModal && (
                <NextAvailableConfig
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    isEdit={!!nextAvailable.selectedDateCourtConfig}
                    closeEditCourtDeskConfig={closeEditCourtDeskConfig}
                    activeFacilitiesAndCourts={activeFacilitiesAndCourts}
                />
            )}
            {nextAvailable.showAddDrawsSidePanel && (
                <AddDrawsSidePanel
                    className={styles.addDivisionsSidePanel}
                    eventId={event?.id}
                    eventAllottedMatchTimes={event?.matchTimes}
                    sportTypeId={event?.sportTypeId}
                    utrTypeId={event?.utrTypeId}
                    eventSchedule={event?.eventSchedule}
                    closeSidePanel={onClickAddDraws}
                />
            )}
            {drawToEditSettings && (
                <DrawEditConfig
                    eventId={event?.id}
                    eventSchedule={event?.eventSchedule}
                    draw={drawToEditSettings}
                    closeSideBar={closeDrawSettingsEdit}
                    sportTypeId={event?.sportTypeId}
                    eventTypeId={event?.eventTypeId}
                    eventAllottedMatchTimes={event?.matchTimes}
                    scheduleTypeId={drawScheduleTypeIds.NEXT_AVAILABLE}
                    drawDivision={drawDivision}
                    allowMomentTimeZoneOverride={true}
                />
            )}
            {matchToPostScore && (
                <MatchPostScoreSidePanel
                    eventId={event?.id}
                    sportTypeId={event?.sportTypeId}
                    utrTypeId={event?.utrTypeId}
                    eventSchedule={event?.eventSchedule}
                    className={styles.matchSettings}
                    match={matchToPostScore}
                    officials={officials}
                    playersDictByMemberId={players?.playersDictByMemberId}
                    teamsDict={teams?.teamsDict}
                    closeSideBar={onClosePostScore}
                    onPostSubmit={onPostScoreSubmit}
                    isSubmitting={nextAvailable.postScore?.isSaving}
                    savingError={nextAvailable.postScore?.error}
                    clubTierType={event.club?.clubTierType || event.club?.tierType || event.club?.tierTypeId}
                />
            )}
            {nextAvailable.matchStartedModal?.show && (
                <MatchStartedPopup eventId={event?.id} />
            )}
            {actionConfirmPopup.show && actionConfirmPopup.type === ConfirmPopupTypes.REMOVE && (
                <ConfirmationPopup
                    title="Remove Match?"
                    description="Are you sure you want to remove this match from the play?"
                    cancelText="No, Cancel"
                    confirmText="Yes, Remove"
                    onConfirm={onConfirmConfirmationPopup}
                    onCancel={onCancelConfirmationPopup}
                    data-testid="modal.next-available.confirm-remove-match"
                />
            )}
            {actionConfirmPopup.show && actionConfirmPopup.type === ConfirmPopupTypes.RE_ASSIGN && (
                <ConfirmationPopup
                    title={`Move match to ${getDestinationCourtName(actionConfirmPopup.data.destinationCourtId)}`}
                    description={`Are you sure you want to move this match to ${getDestinationCourtName(actionConfirmPopup.data.destinationCourtId)}?`}
                    cancelText="No, Cancel"
                    confirmText="Yes, Move"
                    onConfirm={onConfirmConfirmationPopup}
                    onCancel={onCancelConfirmationPopup}
                    data-testid="modal.next-available.confirm-reassign-match"
                />
            )}
        </EventDeskTabLayout>
    );
};

NextAvailable.propTypes = {
    openDrawInEditor: PropTypes.func.isRequired,
};

NextAvailable.defaultProps = {};

export default NextAvailable;
